// Cliente HTTP del admin SPA.
//
// - Inyecta `Authorization: Bearer <accessToken>` desde la sesión guardada.
// - Si recibe 401, intenta UNA renovación contra /admin/auth/refresh y reintenta
//   la request original. Si el refresh también falla, dispara el evento global
//   que tira al usuario al login.

import {
  ADMIN_SESSION_STORAGE_KEY,
  type AdminSession,
} from "../auth/TokenContext";

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  // Internamente lo usamos para no recurrir infinito en el reintento post-refresh.
  _retried?: boolean;
  // Si la request es el propio login/refresh, no queremos auto-refrescar al 401.
  skipAuth?: boolean;
}

function readSession(): AdminSession | null {
  const raw = sessionStorage.getItem(ADMIN_SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminSession;
  } catch {
    return null;
  }
}

function writeSession(s: AdminSession) {
  sessionStorage.setItem(ADMIN_SESSION_STORAGE_KEY, JSON.stringify(s));
}

function clearSession() {
  sessionStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
}

// Compartimos una sola promesa de refresh entre requests concurrentes para no
// quemar el rate-limit cuando varias páginas se quejan de 401 al mismo tiempo.
let refreshInflight: Promise<AdminSession | null> | null = null;

async function refreshSession(): Promise<AdminSession | null> {
  const current = readSession();
  if (!current?.refreshToken) return null;
  if (refreshInflight) return refreshInflight;

  refreshInflight = (async () => {
    try {
      const res = await fetch(buildUrl("/admin/auth/refresh").toString(), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        accessToken: string;
        refreshToken: string;
        user: AdminSession["user"];
      };
      const next: AdminSession = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      };
      writeSession(next);
      return next;
    } catch {
      return null;
    } finally {
      refreshInflight = null;
    }
  })();

  return refreshInflight;
}

// Base URL de la API. En prod normalmente apunta a otro subdominio
// (ej. https://novasispay-api.factupy.es/v1). En dev, default a /v1 same-origin.
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/v1";

function buildUrl(path: string): URL {
  // Soporta tanto VITE_API_BASE_URL absoluto (https://...) como relativo (/v1).
  if (/^https?:\/\//i.test(API_BASE)) {
    return new URL(API_BASE.replace(/\/+$/, "") + path);
  }
  const base = API_BASE.replace(/\/+$/, "");
  return new URL(base + path, window.location.origin);
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const session = readSession();
  const url = buildUrl(path);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (session?.accessToken && !opts.skipAuth) {
    headers.Authorization = `Bearer ${session.accessToken}`;
  }

  const res = await fetch(url.toString(), {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !opts.skipAuth && !opts._retried) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return apiFetch<T>(path, { ...opts, _retried: true });
    }
    clearSession();
    window.dispatchEvent(new CustomEvent("novasis-admin:unauthorized"));
  }

  const text = await res.text();
  const data = text ? safeJson(text) : undefined;

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    if (data && typeof data === "object" && "message" in data) {
      const m = (data as { message?: unknown }).message;
      if (typeof m === "string" && m) message = m;
    }
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}
function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
